/**
 * What the manifest tree shows on the Leadership Brief's node, when a tenant's
 * GUS publishes it: the name people see (GUS's manifest may still call it the
 * executive view), where its settings are edited, and whether that GUS build
 * agrees that an absent switch means off.
 */
import React from 'react';
import { FlagManifest, FlagNode } from '../../services/api';
import { Decorate, Notice, NoticeTitle } from '../flags/FlagTree';
import { DEFAULT_LABEL, EXECUTIVE_PATH, PANEL_KEYS, executiveNode } from './executiveSettings';

function findNode(nodes: FlagNode[] | undefined, path: string): FlagNode | undefined {
  for (const n of nodes || []) {
    if (n?.path === path) return n;
    const inner = findNode(n?.children, path);
    if (inner) return inner;
  }
  return undefined;
}

/**
 * True when this tenant's GUS build shows the tab with no switch stored, false
 * when it agrees it is off, null when its manifest does not describe the tab.
 */
export function manifestSaysExecutiveOn(manifest: FlagManifest): boolean | null {
  const node = findNode(manifest.modules, EXECUTIVE_PATH);
  if (!node) return null;
  const enabled = (node.leaves || []).find((l) => l.key === 'enabled');
  return enabled ? enabled.default !== false : null;
}

export function executiveDecorate(manifest: FlagManifest, storedFlags: Record<string, any>): Decorate {
  const defaultOn = manifestSaysExecutiveOn(manifest);
  const stored = executiveNode(storedFlags);
  const switchStored = typeof stored === 'boolean'
    || (stored !== null && typeof stored === 'object' && 'enabled' in (stored as object)
      && (stored as Record<string, unknown>).enabled !== undefined);
  return (node) => {
    if (node.path !== EXECUTIVE_PATH) return undefined;
    return {
      badges: [{ tone: 'info', text: DEFAULT_LABEL.en, textAr: DEFAULT_LABEL.ar }],
      declaredKeys: PANEL_KEYS,
      notices: (
        <>
          <Notice $tone="info">
            Its settings (AI-written sentences, starting KPIs, the report&apos;s project name, fixed baselines) are
            edited in the Leadership Brief section above.
            <span lang="ar" dir="rtl" style={{ display: 'block' }}>
              تُحرَّر إعداداته (الجمل التي يكتبها الذكاء الاصطناعي، والمؤشرات الأولى، واسم المشروع في التقرير، وخطوط
              الأساس الثابتة) في قسم الموجز القيادي أعلاه.
            </span>
          </Notice>
          {defaultOn && !switchStored && (
            <Notice $tone="warning">
              <NoticeTitle>This GUS build shows the tab when the switch is not set</NoticeTitle>
              The Leadership Brief is meant to be off until an admin turns it on, and the portal reads it that way.
              This tenant&apos;s GUS still declares the opposite default, so save an explicit off to keep it hidden.
              <span lang="ar" dir="rtl" style={{ display: 'block' }}>
                يُفترض أن يبقى الموجز القيادي معطّلًا حتى يفعّله أحد المشرفين، وهكذا تقرؤه البوابة. لكن GUS لدى هذا
                المستأجر ما زال يعلن الإعداد الافتراضي المعاكس، فاحفظ قيمة «معطّل» صراحةً لإبقائه مخفيًا.
              </span>
            </Notice>
          )}
        </>
      ),
    };
  };
}
